import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import Swal from 'sweetalert2';
import ChargeModal from "../components/ModalCobro";
import Ticket from '../components/Ticket';

const CATEGORIES = [
  { value: 'todas', label: '🗂️ All' },
  { value: 'plato', label: '🍽️ Entree' },
  { value: 'pollo', label: '🍗 Chicken' },
  { value: 'mariscos', label: '🦐 Seafood' },
  { value: 'carnes', label: '🥩 Meats' },
  { value: 'sopas', label: '🍜 Soups' },
  { value: 'especiales', label: '⭐ Specials' },
  { value: 'acompañamientos', label: '🍚 Sides' },
  { value: 'bebida', label: '🥤 Drinks' },
];

export default function Diner() {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('todas');
  const [order, setOrder] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showChargeModal, setShowChargeModal] = useState(false);
  const [saleResult, setSaleResult] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const res = await api.get('/restaurante/productos');
      setProducts(res.data);
    } catch (error) {
      Swal.fire('Error', 'Could not load products', 'error');
    } finally {
      setLoading(false);
    }
  };

  const addProduct = (product) => {
    setOrder((prev) => {
      const existing = prev.find((i) => i.id === product.id);
      if (existing) {
        return prev.map((i) =>
          i.id === product.id
            ? { ...i, cantidad: i.cantidad + 1, subtotal: (i.cantidad + 1) * parseFloat(i.precio_venta) }
            : i
        );
      }
      return [...prev, { ...product, cantidad: 1, subtotal: parseFloat(product.precio_venta) }];
    });
  };

  const removeOne = (productId) => {
    setOrder((prev) => {
      const existing = prev.find((i) => i.id === productId);
      if (existing.cantidad === 1) {
        return prev.filter((i) => i.id !== productId);
      }
      return prev.map((i) =>
        i.id === productId
          ? { ...i, cantidad: i.cantidad - 1, subtotal: (i.cantidad - 1) * parseFloat(i.precio_venta) }
          : i
      );
    });
  };

  const removeProduct = (productId) => {
    setOrder((prev) => prev.filter((i) => i.id !== productId));
  };

  const clearOrder = () => {
    setOrder([]);
  };

  const total = order.reduce((acc, i) => acc + i.subtotal, 0);

  const itemsForCharge = order.map((item) => ({
    uniqueKey: `${item.id}-${Date.now()}`,
    product_id: item.id,
    presentation_id: null,
    codigo: item.codigo || `PROD-${item.id}`,
    nombre: item.nombre,
    precio_unitario: parseFloat(item.precio_venta),
    cantidad: item.cantidad,
    subtotal: item.subtotal,
    is_taxable: Boolean(item.is_taxable),
  }));

  const checkout = () => {
    if (order.length === 0) {
      Swal.fire('Notice', 'Add at least one product', 'warning');
      return;
    }
    setShowChargeModal(true);
  };

  const filteredProducts = products.filter((p) => {
    const matchesSearch = p.nombre.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = activeCategory === 'todas' || p.categoria === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const getPhotoUrl = (photoPath) => {
    if (!photoPath) return null;
    if (photoPath.startsWith('http')) return photoPath;
    return `https://altivawebusa.com/BACK/public/storage/${photoPath}`;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <div className="p-3" style={{ height: '100vh' }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">🍽️ Diner</h4>
        <strong>Total: ${total.toFixed(2)}</strong>
      </div>

      <div className="row h-100">
        {/* Products */}
        <div className="col-7 border-end">
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
            {CATEGORIES.map((cat) => (
              <button
                key={cat.value}
                onClick={() => setActiveCategory(cat.value)}
                style={{
                  padding: '5px 12px',
                  borderRadius: '16px',
                  border: '2px solid',
                  borderColor: activeCategory === cat.value ? '#667eea' : '#e2e8f0',
                  background: activeCategory === cat.value ? '#667eea' : 'white',
                  color: activeCategory === cat.value ? 'white' : '#4a5568',
                  fontWeight: 600,
                  fontSize: '12px',
                  cursor: 'pointer',
                }}
              >
                {cat.label}
              </button>
            ))}
          </div>

          <input
            type="text"
            className="form-control mb-3"
            placeholder="Search item..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <div className="row g-2" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
            {filteredProducts.map((product) => (
              <div key={product.id} className="col-4">
                <div
                  className="card text-center"
                  style={{
                    cursor: 'pointer',
                    borderRadius: '10px',
                    overflow: 'hidden',
                    transition: 'transform 0.1s',
                  }}
                  onClick={() => addProduct(product)}
                >
                  {product.foto ? (
                    <img
                      src={getPhotoUrl(product.foto)}
                      alt={product.nombre}
                      style={{ width: '100%', height: '80px', objectFit: 'cover' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: '100%',
                        height: '80px',
                        background: '#f7fafc',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '28px',
                      }}
                    >
                      🍽️
                    </div>
                  )}
                  <div className="p-2">
                    <small className="fw-bold d-block" style={{ fontSize: '12px', lineHeight: '1.2' }}>
                      {product.nombre}
                    </small>
                    <small className="text-success fw-bold d-block" style={{ marginTop: '4px' }}>
                      ${parseFloat(product.precio_venta).toFixed(2)}
                    </small>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Current order */}
        <div className="col-5">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">Current Order</h6>
            {order.length > 0 && (
              <button className="btn btn-sm btn-outline-danger" onClick={clearOrder}>
                🗑️ Clear
              </button>
            )}
          </div>

          {order.length === 0 ? (
            <p className="text-muted text-center mt-4">No items added yet</p>
          ) : (
            <>
              <div style={{ maxHeight: '55vh', overflowY: 'auto' }}>
                {order.map((item) => (
                  <div key={item.id} className="d-flex justify-content-between align-items-center border-bottom py-2">
                    <div style={{ flex: 1 }}>
                      <small className="fw-bold d-block">{item.nombre}</small>
                      <small className="text-muted">${parseFloat(item.precio_venta).toFixed(2)} each</small>
                    </div>
                    <div className="d-flex align-items-center gap-1">
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        style={{ padding: '2px 8px', fontSize: '14px' }}
                        onClick={() => removeOne(item.id)}
                      >
                        −
                      </button>
                      <span style={{ minWidth: '24px', textAlign: 'center', fontWeight: 600 }}>{item.cantidad}</span>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        style={{ padding: '2px 8px', fontSize: '14px' }}
                        onClick={() => addProduct(item)}
                      >
                        +
                      </button>
                      <span className="fw-bold ms-2" style={{ minWidth: '60px', textAlign: 'right' }}>
                        ${item.subtotal.toFixed(2)}
                      </span>
                      <button
                        className="btn btn-sm btn-outline-danger ms-1"
                        style={{ padding: '2px 6px', fontSize: '12px' }}
                        onClick={() => removeProduct(item.id)}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="border-top pt-2 mt-2">
                <div className="d-flex justify-content-between mb-3">
                  <strong>Total:</strong>
                  <strong style={{ fontSize: '18px', color: '#38a169' }}>${total.toFixed(2)}</strong>
                </div>
                <button className="btn btn-success w-100" onClick={checkout}>
                  💰 Create Sale
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {showChargeModal && (
        <ChargeModal
          items={itemsForCharge}
          total={total}
          onSuccess={(data) => {
            setSaleResult(data);
            setShowChargeModal(false);
            setOrder([]);
          }}
          onCancel={() => setShowChargeModal(false)}
        />
      )}

      {saleResult && <Ticket invoice={saleResult} onAfterPrint={() => setSaleResult(null)} />}
    </div>
  );
}